import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Quote } from "lucide-react";
import { BOOKING_URL, STATS, SOLUTIONS, FRAMEWORK, FEATURED_PROGRAMS, TESTIMONIALS, CLIENT_LOGOS, INSIGHTS } from "@/data/site";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="container-cc">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7 fade-up">
              <div className="overline mb-8"><span className="kicker-line" />Employee Experience · Workplace Culture</div>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                Culture isn't built in <span className="italic text-[#A60201]">boardrooms.</span><br />
                It's built through <span className="italic">experiences.</span>
              </h1>
              <p className="mt-8 text-lg text-[#525252] max-w-xl leading-relaxed">
                We partner with CHROs and leadership teams to transform workplace culture — through employee experience programs, cultural IPs, engagement, wellness, learning and purpose-driven experiences.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="hero-book-call-btn" className="btn-primary">
                  Book a Discovery Call <ArrowUpRight size={16} />
                </a>
                <Link to="/solutions" data-testid="hero-explore-solutions-btn" className="btn-secondary">
                  Explore Solutions <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 fade-up d2">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/8117466/pexels-photo-8117466.jpeg"
                  alt="Diverse team collaborating in a modern workspace"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur px-6 py-5 border-t border-black/5">
                  <div className="overline mb-2">Strategic Culture Partner</div>
                  <div className="font-serif-display text-2xl leading-tight">Trusted by 180+ enterprise HR & leadership teams.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-10 border-y border-[#E5E5E5] bg-white overflow-hidden">
        <div className="container-cc mb-6">
          <div className="overline">Trusted by people-first organizations</div>
        </div>
        <div className="marquee-track">
          {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
            <div key={i} className="px-10 font-serif-display text-2xl text-[#525252] whitespace-nowrap">{logo}</div>
          ))}
        </div>
      </section>

      {/* IMPACT METRICS */}
      <section className="section">
        <div className="container-cc">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="bg-[#FAFAFA] p-8 md:p-10">
                <div className="font-serif-display text-5xl md:text-6xl text-[#A60201] tracking-tight">{s.value}</div>
                <div className="mt-3 text-sm text-[#525252] font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="section bg-white border-y border-[#E5E5E5]">
        <div className="container-cc">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="overline mb-6"><span className="kicker-line" />Solutions</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">A strategic suite for the entire employee experience.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-[#525252] text-lg leading-relaxed">
                Eight integrated solutions across culture, engagement, learning, leadership and purpose — designed to be deployed individually or as a connected program.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {SOLUTIONS.map((s, i) => (
              <Link
                key={s.slug}
                to="/solutions"
                data-testid={`solution-card-${s.slug}`}
                className="group bg-white p-7 lg:p-8 hover:bg-[#FFE8D9] transition-colors min-h-[260px] flex flex-col"
              >
                <div className="overline text-[#A60201]">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-serif-display text-2xl mt-4 leading-tight">{s.title}</div>
                <div className="text-sm text-[#525252] mt-3 leading-relaxed flex-1">{s.summary}</div>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] group-hover:gap-3 transition-all">
                  Learn more <ArrowUpRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY + FRAMEWORK */}
      <section className="section">
        <div className="container-cc">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="overline mb-6"><span className="kicker-line" />Why CraftCulture</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">Strategy. Creativity. Execution. In one trusted partner.</h2>
              <p className="mt-8 text-[#525252] text-lg leading-relaxed">
                We sit between the strategic clarity of consulting, the creative depth of design studios, and the operational excellence of large-scale program partners — built specifically for HR and leadership teams.
              </p>
              <Link to="/about" className="link-underline mt-10" data-testid="why-learn-more">Our story <ArrowRight size={16} /></Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
                {FRAMEWORK.map((f) => (
                  <div key={f.step} data-testid={`framework-${f.step}`} className="bg-[#FAFAFA] p-8">
                    <div className="font-serif-display text-5xl text-[#A60201]">{f.step}</div>
                    <div className="mt-4 font-serif-display text-2xl">{f.title}</div>
                    <div className="mt-3 text-sm text-[#525252] leading-relaxed">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROGRAMS */}
      <section className="section bg-[#0A0A0A] text-white">
        <div className="container-cc">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline text-white/60 mb-6"><span className="kicker-line" />Featured Programs</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">Signature programs HR leaders ask for, again and again.</h2>
            </div>
            <Link to="/solutions" className="inline-flex items-center gap-2 text-[#FEBE98] border-b border-[#FEBE98] pb-1 hover:gap-3 transition-all" data-testid="featured-view-all">
              View all solutions <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FEATURED_PROGRAMS.map((p, i) => (
              <div key={i} data-testid={`program-${i}`} className="bg-[#0A0A0A] p-7 lg:p-8 hover:bg-[#1A1A1A] transition-colors min-h-[200px]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#FEBE98]">{p.tag}</div>
                <div className="font-serif-display text-2xl mt-3 leading-tight">{p.title}</div>
                <div className="text-sm text-white/70 mt-3 leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="container-cc">
          <div className="overline mb-6"><span className="kicker-line" />What HR Leaders Say</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] max-w-3xl mb-14">In their words — not ours.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="bg-white p-8 lg:p-10">
                <Quote size={28} className="text-[#A60201] mb-6" />
                <p className="font-serif-display text-2xl leading-snug">{t.quote}</p>
                <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-sm text-[#525252]">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THOUGHT LEADERSHIP */}
      <section className="section bg-white border-y border-[#E5E5E5]">
        <div className="container-cc">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="overline mb-6"><span className="kicker-line" />Insights</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] max-w-2xl">Field notes on culture, engagement and the future of work.</h2>
            </div>
            <Link to="/insights" className="link-underline" data-testid="insights-view-all">All insights <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {INSIGHTS.slice(0, 3).map((post) => (
              <Link key={post.slug} to={`/insights/${post.slug}`} data-testid={`insight-card-${post.slug}`} className="group">
                <div className="aspect-[4/3] overflow-hidden mb-5 bg-[#F3F4F6]">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="overline text-[#A60201] mb-2">{post.category}</div>
                <h3 className="font-serif-display text-2xl leading-tight group-hover:text-[#A60201] transition-colors">{post.title}</h3>
                <div className="text-sm text-[#525252] mt-3">{post.date} · {post.readTime}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section">
        <div className="container-cc">
          <div className="bg-[#FEBE98] p-10 md:p-16 lg:p-20">
            <div className="max-w-3xl">
              <div className="overline mb-6">Final Word</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05]">Let's build a workplace employees love belonging to.</h2>
              <p className="mt-6 text-lg text-[#0A0A0A]/80 max-w-xl">
                A 30-minute discovery call is the simplest way to explore how we can partner with your culture and people priorities.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="final-cta-book-call" className="btn-primary">
                  Book a Discovery Call <ArrowUpRight size={16} />
                </a>
                <Link to="/contact" data-testid="final-cta-contact" className="btn-secondary">
                  Or write to us <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
